<template>
  <ChartCard :data-widget-id="widgetId">
    <ChartHeader
      :label="widgetLabel"
      :widget-download-id="widgetId"
      tooltip-key="dropoff-by-stage"
    />
    <ChartContent
      height="290px"
      :empty-data="emptyData || noWidgetData"
      :error="error"
      :loading="loading"
      type="doughnutChart"
      class="px-3 pt-8 pb-0"
    >
      <DoughnutChart :id="widgetId" :data="chartData" :options="chartOptions" :plugins="plugins" />
    </ChartContent>
  </ChartCard>
</template>

<script setup>
import ChartHeader from '@/components/insights/Chart/ChartHeader.vue';
import ChartCard from '@/components/insights/Chart/ChartCard.vue';
import ChartContent from '@/components/insights/Chart/ChartContent.vue';
import DoughnutChart from '@/components/insights/Chart/DoughnutChart.vue';
import { computed } from 'vue';
import { useColors } from '@/composables/colors';
import { LegendSpacingPlugin } from '@/api/helpers/insights/chartPlugins';
import { donutChartTooltipPluginConfig, donutChartLegendPluginConfig, donutChartScalesOptions } from '@/api/helpers/insights/commonChartConfigs';

const props = defineProps({
  data: {
    type: Object,
    default: () => ({}),
  },
  emptyData: {
    type: Boolean,
    default: false,
  },
  error: {
    type: Boolean,
    default: false,
  },
  loading: {
    type: Boolean,
    default: false,
  },
});

const widgetLabel = 'At what stage do most drop-offs occur?';

const plugins = [LegendSpacingPlugin];

const widgetId = 'most-drop-offs';

const {
  stageScreen,
  stageAssessment,
  stageOffer,
  stageApplication,
} = useColors();

const colors = [
  stageApplication.value,
  stageScreen.value,
  stageAssessment.value,
  stageOffer.value,
];

const noWidgetData = computed(() => !props?.data.dropped_out
    || Object.keys(props.data.dropped_out)
      .every((key) => !props.data.dropped_out[key]));

const chartData = computed(() => {
  const { dropped_out: droppedOut } = props.data || {};
  const totalDroppedOut = droppedOut.review
    + droppedOut.screen
    + droppedOut.assessment
    + droppedOut.offer
    + droppedOut.hired;

  const stages = [
    { label: 'Application', value: droppedOut.review },
    { label: 'Screening', value: droppedOut.screen },
    { label: 'Assessment', value: droppedOut.assessment },
    { label: 'Offered', value: droppedOut.offer },
  ];

  const filteredStages = stages.filter((stage) => stage.value);

  return {
    labels: filteredStages.map((stage) => stage.label),
    datasets: [
      {
        label: '',
        backgroundColor: colors.slice(0, filteredStages.length),
        borderColor: colors.slice(0, filteredStages.length),
        data: filteredStages.map((stage) => (stage.value / totalDroppedOut) * 100),
      },
    ],
  };
});

const chartOptions = computed(() => ({
  maintainAspectRatio: false,
  responsive: true,
  aspectRatio: 1,
  plugins: {
    tooltip: donutChartTooltipPluginConfig(chartData, widgetId),
    legend: donutChartLegendPluginConfig(colors),
  },
  scales: donutChartScalesOptions,
}));
</script>
